import { observer } from 'mobx-react';

import { AdminSection } from '@assets/component/AdminSection/AdminSection';
import {
    BreadcrumbItem,
    Breadcrumbs
} from '@assets/component/Breadcrumbs/Breadcrumbs';
import { DealCompare } from '@assets/component/DealCompare/DealCompare';
import { DealDashboard } from '@assets/component/DealDashboard/DealDashboard';
import { DealDetails } from '@assets/component/DealDetails/DealDetails';
import { DealScoreBreakdown } from '@assets/component/DealScoreBreakdown/DealScoreBreakdown';
import DealViewSidebar from '@assets/component/DealViewSidebar/DealViewSidebar';
import { Header } from '@assets/component/Header/Header';
import { TaskPage } from '@assets/component/TaskPage/TaskPage';
import { Flex } from '@assets/component/UI/Flex/Flex';
import { useOnPageLoad } from '@assets/hooks/useOnPageLoad';
import {
    getDealTabLabel,
    UIDealDetailsTabs,
    useClientSettingStore,
    useSetPageTitle,
    useStore,
    useStoreUI,
    useTasksStore
} from '@assets/model/Store';
import { useNavUtils } from '@assets/utils/NavUtils';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DealChat from './DealChat';
import { DealFlowise } from './DealFlowise';
import DealNarrativePage from './DealNarrative';
import { DealOutputs } from './DealOutputs';
import { WorkStreamPage } from './WorkstreamsPage';
import { ProjectFilesPage } from '@assets/component/ProjectFiles/ProjectFilesPage';

require('./DealView.css');

const DealView = observer(() => {
    const { id } = useParams();
    const store = useStore();
    const uiStore = useStoreUI();
    const clientStore = useClientSettingStore();
    const taskStore = useTasksStore();
    const navUtils = useNavUtils();

    useEffect(() => {
        if (!store.isLoaded) {
            return;
        }
        if (!store.reportDataDB.hasOwnProperty(id)) {
            navUtils.home();
        }
    }, [store.isLoaded]);

    useOnPageLoad('DEAL_VIEW', () => {
        uiStore.resetDealView();
    });

    useSetPageTitle(store.query?.value('company_name') ?? '');

    useEffect(() => {
        if (id) {
            console.log('Setting report id to', id);
            store.selectedReportId = id;
        }
    }, [id]);

    useEffect(() => {
        if (!uiStore.isKeyTabDown) {
            return;
        }

        const values = Object.values(UIDealDetailsTabs);
        const currentIndex = values.indexOf(uiStore.dealViewTab);
        uiStore.dealViewTab =
            values[
                (currentIndex +
                    (uiStore.isKeyShiftKeyDown ? -1 : 1) +
                    values.length) %
                    values.length
            ];
    }, [uiStore.isKeyTabDown]);

    if (store.query == null) {
        return null;
    }

    const returnActivePanel = () => {
        switch (uiStore.dealViewTab) {
            case UIDealDetailsTabs.DASHBOARD:
                return <DealDashboard />;
            case UIDealDetailsTabs.DETAILS:
                return <DealDetails />;
            case UIDealDetailsTabs.COMPARE:
                return <DealCompare />;
            case UIDealDetailsTabs.SCORE_BREAKDOWN:
                return <DealScoreBreakdown />;
            case UIDealDetailsTabs.A1_NARRATIVE:
                return <DealNarrativePage />;
            case UIDealDetailsTabs.WORKSTREAMS:
                return <WorkStreamPage />;
            case UIDealDetailsTabs.TASK_VIEW_DEBUG:
                return <TaskPage manualSetTaskId="write_short_story" />;
            case UIDealDetailsTabs.PROJECT_FILES:
                return <ProjectFilesPage />;
            case UIDealDetailsTabs.FLOWISE:
                return <DealFlowise />;
            case UIDealDetailsTabs.OUTPUTS:
                return <DealOutputs />;
            default:
                return <div>Unknown Tab</div>;
        }
    };
    const do_we_have_pdf = store.query.report_has_pdf;

    const goToCase = () => {
        uiStore.dealViewTab = UIDealDetailsTabs.WORKSTREAMS;
        uiStore.workstreamModuleTaskId = null;
    };
    const breadcrumbItems: BreadcrumbItem[] = [
        { label: 'All cases', onClick: () => navUtils.home() },
        { label: store.company_name || 'Case', onClick: goToCase },
        { label: getDealTabLabel(uiStore.dealViewTab) }
    ];

    const openModuleTaskId = uiStore.workstreamModuleTaskId;
    if (
        uiStore.dealViewTab === UIDealDetailsTabs.WORKSTREAMS &&
        openModuleTaskId
    ) {
        const moduleName =
            taskStore.get_task_definition(openModuleTaskId)?.name ??
            openModuleTaskId;
        // Make "Workstreams" clickable (back to overview) and append the module.
        breadcrumbItems[breadcrumbItems.length - 1].onClick = () => {
            uiStore.workstreamModuleTaskId = null;
        };
        breadcrumbItems.push({ label: moduleName });
    }

    return (
        <>
            <DealViewSidebar />
            <div
                className={'d-flex justify-content-center'}
                style={{
                    height: '100%',
                    backgroundColor: 'white'
                }}
            >
                <AdminSection />
                <Flex direction="column" className={'h-100 w-100'}>
                    <Header
                        disableTicker={true}
                        showDealDbLink={false}
                        showScore={
                            true &&
                            clientStore.showAggregateScores &&
                            do_we_have_pdf
                        }
                        showShadow={true}
                    />
                    <Flex
                        direction="column"
                        style={{
                            paddingLeft:
                                'calc(var(--current-sidebar-width) + var(--main-container-side-padding))',
                            paddingRight: 'var(--main-container-side-padding)',
                            paddingTop: '24px',
                            gap: '24px',
                            transition: 'padding-left 0.2s ease',
                            position: 'relative',
                            backgroundColor: 'var(--content-background-color)',
                            borderTop: '1px solid #eaecf0',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                            width: '100%'
                        }}
                    >
                        <Breadcrumbs items={breadcrumbItems} />
                        <div
                            className="d-flex flex-column"
                            style={{
                                flex: 1,
                                minHeight: 0,
                                width: '100%'
                            }}
                        >
                            {returnActivePanel()}
                        </div>
                    </Flex>
                </Flex>
            </div>
        </>
    );
});

export default DealView;
